// BoardModals.tsx — Sprint 15. Four modals as named exports:
// MemberEditModal, MeetingEditModal, ResolutionEditModal,
// MeetingMinutesModal. Pattern matches ParkingModals /
// WorkOrderModals — Form instances and the row-being-acted-on
// state stay in the orchestrator, modals are stateless.
import React from 'react';
import {
  Button, Col, Divider, Form, FormInstance, Input, Modal, Row, Select,
  Statistic, Tag, Timeline, Typography,
} from 'antd';
import { MailOutlined, PhoneOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import type { BoardMember, BoardMeeting, BoardResolution, BoardRole, MinuteItem } from '../../types';
import {
  BOARD_ROLE_OPTIONS, MEETING_STATUS_CONFIG, MEETING_TYPE_CONFIG,
  RESOLUTION_CATEGORY_OPTIONS,
} from './boardTypes';

const { Option } = Select;
const { TextArea } = Input;
const { Text, Paragraph } = Typography;

// ─── 1. Add / Edit Board Member ──────────────────────────────────────────────

export interface MemberEditModalProps {
  open:           boolean;
  editingMember:  BoardMember | null;
  form:           FormInstance;
  onCancel:       () => void;
  onSubmit:       () => void;
}

export const MemberEditModal: React.FC<MemberEditModalProps> = ({
  open, editingMember, form, onCancel, onSubmit,
}) => (
  <Modal
    open={open}
    onCancel={onCancel}
    title={editingMember ? 'Edit Board Member' : 'Add Board Member'}
    onOk={onSubmit}
    width={560}
    okText={editingMember ? 'Save Changes' : 'Add Member'}
  >
    <Form form={form} layout="vertical">
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item name="name" label="Full Name" rules={[{ required: true }]}>
            <Input placeholder="e.g. Jane Smith" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="unitNumber" label="Unit Number" rules={[{ required: true }]}>
            <Input placeholder="e.g. 401" />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item name="role" label="Board Role" rules={[{ required: true }]}>
            <Select>
              {(BOARD_ROLE_OPTIONS as ReadonlyArray<BoardRole>).map(r => (
                <Option key={r} value={r}>{r}</Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="isActive" label="Status" rules={[{ required: true }]}>
            <Select>
              <Option value={true}>Active</Option>
              <Option value={false}>Inactive</Option>
            </Select>
          </Form.Item>
        </Col>
      </Row>
      <Form.Item
        name="email" label="Email Address"
        rules={[{ required: true }, { type: 'email' }]}
      >
        <Input prefix={<MailOutlined />} placeholder="jane.smith@example.com" />
      </Form.Item>
      <Form.Item name="phone" label="Phone Number">
        <Input prefix={<PhoneOutlined />} placeholder="(416) 555-0100" />
      </Form.Item>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item name="termStart" label="Term Start" rules={[{ required: true }]}>
            <Input type="date" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="termEnd" label="Term End" rules={[{ required: true }]}>
            <Input type="date" />
          </Form.Item>
        </Col>
      </Row>
    </Form>
  </Modal>
);

// ─── 2. Schedule / Edit Meeting ──────────────────────────────────────────────

export interface MeetingEditModalProps {
  open:            boolean;
  editingMeeting:  BoardMeeting | null;
  form:            FormInstance;
  onCancel:        () => void;
  onSubmit:        () => void;
}

export const MeetingEditModal: React.FC<MeetingEditModalProps> = ({
  open, editingMeeting, form, onCancel, onSubmit,
}) => (
  <Modal
    open={open} onCancel={onCancel}
    title={editingMeeting ? 'Edit Meeting' : 'Schedule Meeting'}
    onOk={onSubmit} width={620}
    okText={editingMeeting ? 'Save' : 'Schedule'}
  >
    <Form form={form} layout="vertical">
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item name="meetingNumber" label="Meeting No." rules={[{ required: true }]}>
            <Input />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="type" label="Meeting Type" rules={[{ required: true }]}>
            <Select>
              {Object.entries(MEETING_TYPE_CONFIG).map(([k, v]) => (
                <Option key={k} value={k}><Tag color={v.color}>{v.label}</Tag></Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>
      <Form.Item name="title" label="Meeting Title" rules={[{ required: true }]}>
        <Input placeholder="e.g. Q2 2026 Board Meeting" />
      </Form.Item>
      <Row gutter={16}>
        <Col span={8}>
          <Form.Item name="date" label="Date" rules={[{ required: true }]}>
            <Input type="date" />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="startTime" label="Start Time" rules={[{ required: true }]}>
            <Input type="time" />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="endTime" label="End Time">
            <Input type="time" />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item name="location" label="Location" rules={[{ required: true }]}>
            <Input placeholder="Party Room, Zoom, etc." />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="quorumRequired" label="Quorum Required (# owners)">
            <Input type="number" min={1} />
          </Form.Item>
        </Col>
      </Row>
      <Form.Item name="status" label="Status">
        <Select>
          {Object.entries(MEETING_STATUS_CONFIG).map(([k, v]) => (
            <Option key={k} value={k}>{v.label}</Option>
          ))}
        </Select>
      </Form.Item>
    </Form>
  </Modal>
);

// ─── 3. Create / Edit Resolution ─────────────────────────────────────────────

export interface ResolutionEditModalProps {
  open:         boolean;
  editingVote:  BoardResolution | null;
  form:         FormInstance;
  onCancel:     () => void;
  onSubmit:     () => void;
}

export const ResolutionEditModal: React.FC<ResolutionEditModalProps> = ({
  open, editingVote, form, onCancel, onSubmit,
}) => (
  <Modal
    open={open} onCancel={onCancel}
    title={editingVote ? 'Edit Resolution' : 'New Resolution / Vote'}
    onOk={onSubmit} width={620}
    okText={editingVote ? 'Save' : 'Create'}
  >
    <Form form={form} layout="vertical">
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item name="voteNumber" label="Resolution No." rules={[{ required: true }]}>
            <Input />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="category" label="Category">
            <Select>
              {RESOLUTION_CATEGORY_OPTIONS.map(c => (
                <Option key={c} value={c}>{c}</Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>
      <Form.Item name="title" label="Resolution Title" rules={[{ required: true }]}>
        <Input />
      </Form.Item>
      <Form.Item name="description" label="Description" rules={[{ required: true }]}>
        <TextArea rows={3} />
      </Form.Item>
      <Row gutter={16}>
        <Col span={8}>
          <Form.Item name="openDate" label="Voting Opens" rules={[{ required: true }]}>
            <Input type="date" />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="closeDate" label="Voting Closes" rules={[{ required: true }]}>
            <Input type="date" />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="totalEligible" label="Eligible Voters">
            <Input type="number" min={1} />
          </Form.Item>
        </Col>
      </Row>
      <Divider plain>Vote Tally (enter when tallying results)</Divider>
      <Row gutter={16}>
        <Col span={8}>
          <Form.Item name="votesFor" label="Votes For">
            <Input type="number" min={0} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="votesAgainst" label="Votes Against">
            <Input type="number" min={0} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="votesAbstain" label="Abstentions">
            <Input type="number" min={0} />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item name="status" label="Status">
            <Select>
              <Option value="open">Open for Voting</Option>
              <Option value="closed">Closed</Option>
            </Select>
          </Form.Item>
        </Col>
      </Row>
      <Form.Item name="notes" label="Notes">
        <TextArea rows={2} />
      </Form.Item>
    </Form>
  </Modal>
);

// ─── 4. Meeting Minutes ──────────────────────────────────────────────────────

export interface MeetingMinutesModalProps {
  open:    boolean;
  meeting: BoardMeeting | null;
  text:    string;
  onTextChange: (next: string) => void;
  onClose:      () => void;
  onAddEntry:   () => void;
}

export const MeetingMinutesModal: React.FC<MeetingMinutesModalProps> = ({
  open, meeting, text, onTextChange, onClose, onAddEntry,
}) => (
  <Modal
    open={open}
    onCancel={onClose}
    title={`Minutes — ${meeting?.title ?? ''}`}
    width={700}
    footer={[
      <Button key="close" onClick={onClose}>Close</Button>,
      <Button key="save" type="primary" onClick={onAddEntry}>Add Entry</Button>,
    ]}
  >
    {meeting && (
      <>
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={8}><Statistic title="Date" value={dayjs(meeting.date).format('MMM D, YYYY')} /></Col>
          <Col span={8}><Statistic title="Time" value={meeting.startTime} /></Col>
          <Col span={8}><Statistic title="Location" value={meeting.location} /></Col>
        </Row>
        <Divider />
        {meeting.minutes.length === 0 ? (
          <Text type="secondary">No minutes recorded yet.</Text>
        ) : (
          <Timeline>
            {meeting.minutes.map((m: MinuteItem) => (
              <Timeline.Item key={m.id}>
                <Text strong>{m.title}</Text>
                <Paragraph style={{ margin: '4px 0' }}>{m.content}</Paragraph>
                {m.actionItems.length > 0 && (
                  <ul style={{ marginLeft: 16 }}>
                    {m.actionItems.map((a, j) => (
                      <li key={j}><Text type="secondary">{a}</Text></li>
                    ))}
                  </ul>
                )}
              </Timeline.Item>
            ))}
          </Timeline>
        )}
        <Divider plain>Add Minutes Entry</Divider>
        <Input.TextArea
          rows={4}
          placeholder="Type minutes content here…"
          value={text}
          onChange={e => onTextChange(e.target.value)}
        />
      </>
    )}
  </Modal>
);

